import './App.css';
import React, { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, ThemeProvider } from '@mui/material';
import { lightTheme } from './Theme';
import Layout from './Layout/index';
import AppCommonActionsWrapper from './Component/Common/appCommonActionsWrapper';
import { getPermission } from './Redux/Actions/RoleManagement';
import localStoreUtil from './ApiSetUp/localstore.util';
import { LOGIN_TOKEN } from './ApiSetUp/AuthApi';
import { useDispatch } from 'react-redux';
import { ProtectedRoutes } from './Routes/ProtectedRoutes';
import { setNavigate } from './ApiSetUp/navigation';

const Login = lazy(() => import('./Pages/Login'));
const ForgotPassword = lazy(() => import('./Pages/ForgotPassword'));
const OtpVerification = lazy(() => import('./Pages/OtpVerification'));
const EnterPassword = lazy(() => import('./Pages/EnterPassword'));
const Dashboard = lazy(() => import('./Pages/Dashboard'));
const VendorManagement = lazy(() => import('./Pages/VendorManagement'));
const Inventory = lazy(() => import('./Pages/Inventory'));
const Unit = lazy(() => import('./Pages/Inventory/Common/Unit'));
const CompanyName = lazy(() => import('./Pages/Inventory/Common/CompanyName'));
const Group = lazy(() => import('./Pages/Inventory/Common/Group'));
const SubGroup = lazy(() => import('./Pages/Inventory/Common/SubGroup'));
const Size = lazy(() => import('./Pages/Inventory/Common/Size'));
const Color = lazy(() => import('./Pages/Inventory/Common/Color'));
const TypeOfPaper = lazy(() => import('./Pages/Inventory/TypeOfPaper'));
const Micron = lazy(() => import('./Pages/Inventory/Micron'));
const ChemicalName = lazy(() => import('./Pages/Inventory/ChemicalName'));
const ChemicalCategory = lazy(() => import('./Pages/Inventory/ChemicalCategory'));
const ChemicalCode = lazy(() => import('./Pages/Inventory/ChemicalCode'));
const PantonNumber = lazy(() => import('./Pages/Inventory/PantonNumber'));
const PurchaseOrder = lazy(() => import('./Pages/PurchaseOrder'));
const PurchaseOrderDocuments = lazy(() => import('./Pages/PurchaseOrderDocuments'));
const Documents = lazy(() => import('./Pages/Documents'));
const PurchaseEntry = lazy(() => import('./Pages/PurchaseEntry'));
const LotNumberList = lazy(() => import('./Pages/PurchaseEntry/LotNumberList'));
const GrnNumber = lazy(() => import('./Pages/PurchaseEntry/GrnNumber'));
const Production = lazy(() => import('./Pages/PurchaseEntry/Production'));
const ProcessingUnitList = lazy(() => import('./Pages/ProcessingUnitList'));
const UserAndRole = lazy(() => import('./Pages/UserAndRole'));
const Notification = lazy(() => import('./Pages/Notification'));
const ReleaseStatus = lazy(() => import('./Pages/Inventory/Common/ReleaseStatus'));
const Product = lazy(() => import('./Pages/Inventory/Common/Product'));
const AccountDashboard = lazy(() => import('./Pages/Account/Dashboard'));
const AccountInsights = lazy(() => import('./Pages/Account/Insights'));
const AccountCustomers = lazy(() => import('./Pages/Account/Customers'));
const AccountCustomerProfile = lazy(() => import('./Pages/Account/Customers/Profile'));
const AccountItems = lazy(() => import('./Pages/Account/Items'));
const AccountInvoice = lazy(() => import('./Pages/Account/Invoice'));
const AccountSettings = lazy(() => import('./Pages/Account/Settings'));

const PageFallback = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240, width: '100%' }}>
    <CircularProgress size={32} sx={{ color: '#E77843' }} />
  </Box>
);

function App() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);

  let login_Token = localStoreUtil.get_data(LOGIN_TOKEN);
  let Time = localStoreUtil.get_data('Time');
  useEffect(() => {
    if (login_Token) {
      const currentTime = new Date().getTime();
      const timeDifference = currentTime - parseInt(Time, 10);
      const oneDay = 24 * 60 * 60 * 1000;
      if (timeDifference > oneDay) {
        localStoreUtil.remove_all();
        navigate('/login');
      }
      (async () => {
        if (typeof window !== 'undefined') {
          const data = await dispatch(getPermission());
          if (data?.payload?.data?.code === 0) {
            navigate('/login');
          }
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login_Token]);

  useEffect(() => {
    if (!login_Token) return;

    let timeoutId;
    const IDLE_TIMEOUT = 60 * 60 * 1000;

    const resetTimer = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        localStoreUtil.remove_all();
        navigate('/login');
      }, IDLE_TIMEOUT);
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll'];
    events.forEach((event) => window.addEventListener(event, resetTimer));
    resetTimer();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      events.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, [login_Token, navigate]);

  return (
    <ThemeProvider theme={lightTheme}>
      <AppCommonActionsWrapper />
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/otp-verification" element={<OtpVerification />} />
          <Route path="/enter-password" element={<EnterPassword />} />

          <Route element={<ProtectedRoutes />}>
            <Route element={<Layout />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/notification" element={<Notification />} />
              <Route path="/vendor-management" element={<VendorManagement />} />
              <Route path="/purchase-order" element={<PurchaseOrder />} />
              <Route path="/purchase-order/po-documents" element={<PurchaseOrderDocuments />} />
              <Route path="/processing-unit-list" element={<ProcessingUnitList />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="/invoice" element={<Navigate to="/account/invoice" replace />} />
              <Route path="/user-role" element={<UserAndRole />} />
              <Route path="/purchase-entry" element={<PurchaseEntry />} />
              <Route path="/purchase-entry/lot-number-list" element={<LotNumberList />} />
              <Route path="/purchase-entry/grn-number" element={<GrnNumber />} />
              <Route path="/purchase-entry/production" element={<Production />} />
              <Route path="/inventory" element={<Inventory />} />
              <Route path="/inventory/common/unit" element={<Unit />} />
              <Route path="/inventory/common/company-name" element={<CompanyName />} />
              <Route path="/inventory/common/group" element={<Group />} />
              <Route path="/inventory/common/sub-group" element={<SubGroup />} />
              <Route path="/inventory/common/size" element={<Size />} />
              <Route path="/inventory/common/color" element={<Color />} />
              <Route path="/inventory/common/release-status" element={<ReleaseStatus />} />
              <Route path="/inventory/type-of-paper" element={<TypeOfPaper />} />
              <Route path="/inventory/micron" element={<Micron />} />
              <Route path="/inventory/chemical-name" element={<ChemicalName />} />
              <Route path="/inventory/chemical-category" element={<ChemicalCategory />} />
              <Route path="/inventory/chemical-code" element={<ChemicalCode />} />
              <Route path="/inventory/pantone-number" element={<PantonNumber />} />
              <Route path="/inventory/product" element={<Product />} />
              <Route path="/account" element={<AccountDashboard />} />
              <Route path="/account/insights" element={<AccountInsights />} />
              <Route path="/account/customers" element={<AccountCustomers />} />
              <Route path="/account/customers/:id" element={<AccountCustomerProfile />} />
              <Route path="/account/items" element={<AccountItems />} />
              <Route path="/account/invoice" element={<AccountInvoice />} />
              <Route path="/account/settings" element={<AccountSettings />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </ThemeProvider>
  );
}

export default App;
